import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  isDevMode,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';

import { Observable, Subject } from 'rxjs';
import {
  filter,
  map,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { CookieService } from 'ngx-cookie-service';
import { MatDialog } from '@angular/material/dialog';

import { FeatureTogglesFacade } from '@cigna/features';
import { RouterFacade } from '@cigna/core/router-state';

import { AuthFacade } from '@cigna/chcp/auth/data-access';
import { UserFacade } from '@cigna/chcp/shared/user-profile-data-access';
import { ShellFacade } from '@cigna/chcp/shell/data-access';
import {
  ENVIRONMENT_CONFIG,
  EnvironmentConfig,
} from '@cigna/chcp/shared/environment-util';
import { RouteData } from '@cigna/chcp/shared/util';
import { LobPreferenceDialogComponent } from '@cigna/chcp/shared/ui';
import { LobPref } from '@cigna/chcp/auth/util';

@Component({
  selector: 'cigna-layout-secure',
  templateUrl: './layout-secure.component.html',
  styleUrls: ['./layout-secure.component.scss'],
})
export class LayoutSecureComponent implements OnInit, OnDestroy {
  firstName$ = this.userFacade.firstName$;
  lastName$ = this.userFacade.lastName$;
  userid$ = this.authFacade.userid$;
  showPasswordChangeWarning$ = this.userFacade.showPasswordChangeWarning$;
  lastPasswordChangeDate$ = this.userFacade.lastPasswordChangeDate$;
  hasUndefinedLobPref$ = this.userFacade.hasUndefinedLobPref$;

  impersonator$ = this.authFacade.isImpersonator$;

  profileNav$ = this.shellFacade.profileNav$.pipe(
    map((data) => data.filter((link) => link.identifier !== 'chcp.inbox')),
  );
  siteNav$ = this.shellFacade.siteNav$;
  alert$ = this.shellFacade.alertSecure$;

  routeData$ = this.routerFacade.data$ as Observable<RouteData>;
  routeUrl$ = this.routerFacade.url$;

  isResourcePage$ = this.routeData$.pipe(
    map((data) => data.routeCode === 'resources'),
  );
  logoLink$ = this.isResourcePage$.pipe(
    map((isResourcePage) =>
      isResourcePage
        ? // temporary fix until the rest of resource library is built
          `${this.envConfig.api.gateways.cdn}assets/evernorth/provider/resourceLibrary/resourceLibrary.html`
        : '/dashboard',
    ),
  );

  isloginUserProfileAWS$ = this.featureToggleFacade.featuresEnabled([
    'loginUserProfileAWS',
  ]);

  onDestroy$ = new Subject<void>();

  isInBrowserPlatform: boolean;

  isDevMode = false;

  constructor(
    public shellFacade: ShellFacade,
    private userFacade: UserFacade,
    private authFacade: AuthFacade,
    private routerFacade: RouterFacade,
    private cookieService: CookieService,
    private featureToggleFacade: FeatureTogglesFacade,
    public dialog: MatDialog,
    @Inject(PLATFORM_ID) platformId: {},
    @Inject(ENVIRONMENT_CONFIG) private envConfig: EnvironmentConfig,
  ) {
    this.isInBrowserPlatform = isPlatformBrowser(platformId);
  }

  ngOnInit() {
    /* FOR LOCAL DEV ONLY */
    if (isDevMode()) {
      this.isDevMode = true;
      this.authFacade.loadMockTokens();
    }

    this.shellFacade.loadNav();
    this.isloginUserProfileAWS$
      .pipe(take(1))
      .subscribe((isloginUserProfileAWS) => {
        if (isloginUserProfileAWS) {
          this.userFacade.getLoginProfile();
        } else {
          this.userFacade.getProfile();
          this.userFacade.loadTins();
        }
      });
    this.shellFacade.loadSecureAlerts();

    // remove deeplink cookie after login unless we are in the middle of mfa flow
    this.routeUrl$
      .pipe(
        filter((url) => !url?.startsWith('/mfa')),
        take(1),
        tap(() => {
          this.cookieService.delete('CHCP_DEEPLINK');
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe();

    this.hasUndefinedLobPref$
      .pipe(take(2), withLatestFrom(this.authFacade.isImpersonator$))
      .subscribe(([hasUndefinedLobPref, isImpersonator]) => {
        if (hasUndefinedLobPref && !isImpersonator) {
          this.dialog
            .open(LobPreferenceDialogComponent, {
              disableClose: true,
              autoFocus: false,
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe(({ lobPref }: { lobPref: LobPref }) => {
              if (lobPref) {
                this.userFacade.updateProfile({ lobPref });
              }
            });
        }
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
